import React from 'react';
import { TrendingUp, Users, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Cases = () => {
  const { language } = useLanguage();

  const cases = [
    {
      company: language === 'es' ? 'Inmobiliaria Premium' : 'Premium Real Estate',
      industry: language === 'es' ? 'Inmobiliario' : 'Real Estate',
      challenge: language === 'es'
        ? 'Baja tasa de contacto con leads de anuncios de Facebook'
        : 'Low contact rate with Facebook ad leads',
      solution: language === 'es'
        ? 'Implementación de Speed to Lead para contacto inmediato'
        : 'Speed to Lead implementation for immediate contact',
      results: [
        { label: language === 'es' ? 'Aumento en conversiones' : 'Conversion increase', value: '+250%' },
        { label: language === 'es' ? 'Tiempo de respuesta' : 'Response time', value: '<45s' },
        { label: language === 'es' ? 'Leads contactados' : 'Contacted leads', value: '98%' }
      ]
    },
    {
      company: language === 'es' ? 'Academia Online' : 'Online Academy',
      industry: language === 'es' ? 'Educación' : 'Education',
      challenge: language === 'es'
        ? 'Falta de seguimiento a prospectos interesados'
        : 'Lack of follow-up with interested prospects',
      solution: language === 'es'
        ? 'Agente de texto para seguimiento automatizado'
        : 'Text agent for automated follow-up',
      results: [
        { label: language === 'es' ? 'Más inscripciones' : 'More enrollments', value: '+180%' },
        { label: language === 'es' ? 'Tasa de respuesta' : 'Response rate', value: '85%' },
        { label: language === 'es' ? 'Tiempo ahorrado' : 'Time saved', value: '20h/sem' }
      ]
    },
    {
      company: language === 'es' ? 'E-commerce Fashion' : 'Fashion E-commerce',
      industry: 'Retail',
      challenge: language === 'es'
        ? 'Carritos abandonados sin recuperación'
        : 'Abandoned carts without recovery',
      solution: language === 'es'
        ? 'Agente de recuperación con campañas personalizadas'
        : 'Recovery agent with personalized campaigns',
      results: [
        { label: language === 'es' ? 'Recuperación de ventas' : 'Sales recovery', value: '+320%' },
        { label: language === 'es' ? 'ROI' : 'ROI', value: '450%' },
        { label: language === 'es' ? 'Clientes reactivados' : 'Reactivated customers', value: '2.5k' }
      ]
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-900/20 via-black to-emerald-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30 mb-6 px-4 py-2">
              {language === 'es' ? 'Historias de éxito' : 'Success stories'}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {language === 'es' ? 'Casos de' : 'Success'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                {language === 'es' ? 'Éxito' : 'Cases'}
              </span>
            </h1>
            <p className="text-xl text-gray-200">
              {language === 'es'
                ? 'Descubre cómo nuestros clientes han transformado sus negocios con GERVIS'
                : 'Discover how our clients have transformed their businesses with GERVIS'}
            </p>
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="space-y-12 max-w-5xl mx-auto">
            {cases.map((caseItem, index) => (
              <Card key={index} className="bg-gradient-to-br from-gray-900 to-black border-green-900/30 hover:border-green-600/50 transition-all duration-300">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                    <div>
                      <Badge className="bg-green-600/20 text-green-400 border-green-600/30 mb-3">
                        {caseItem.industry}
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white">{caseItem.company}</h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h4 className="text-green-400 font-semibold mb-2">
                        {language === 'es' ? 'Desafío' : 'Challenge'}
                      </h4>
                      <p className="text-gray-200">{caseItem.challenge}</p>
                    </div>
                    <div>
                      <h4 className="text-green-400 font-semibold mb-2">
                        {language === 'es' ? 'Solución' : 'Solution'}
                      </h4>
                      <p className="text-gray-200">{caseItem.solution}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-green-400 font-semibold mb-4">
                      {language === 'es' ? 'Resultados' : 'Results'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {caseItem.results.map((result, rIndex) => (
                        <div key={rIndex} className="bg-black/50 rounded-lg p-4 border border-green-900/20">
                          <div className="text-3xl font-bold text-green-400 mb-1">{result.value}</div>
                          <div className="text-sm text-gray-200">{result.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cases;